import { Injectable } from '@angular/core';
import { BaseApiService } from '@ep/shared';
import { catchError, map, Observable, of, shareReplay, switchMap } from 'rxjs';
import { DeviceTypes } from '@shared/enums';
import { getEnvironment } from '@shared/utils';

export interface DeviceResponse {
  deviceId: number;
  name: string;
  deviceType: DeviceTypes;
  statusType: number;
  deviceTrackings: DeviceTrackingsResponse;
  connectionId?: null;
  settings?: string | null;
  serialNumber?: string | null;
}

export interface DeviceSignalRResponse {
  deviceId: number;
  name: string;
  deviceType: DeviceTypes;
  statusType: number;
  deviceTrackings: DeviceTrackingResponse[];
  connectionId?: null;
}

export interface DevicesResponse {
  $values: DeviceResponse[];
}

export interface DeviceTrackingsResponse {
  $values: DeviceTrackingResponse[];
}

export interface DeviceTrackingResponse {
  deviceTrackingId: number;
  deviceId: number;
  applicationVersion: string;
  make: string;
  model: string;
  serialNumber: string;
  createdOn: Date;
}

export interface DeviceTracking {
  DeviceTrackingId: number;
  DeviceId: number;
  ApplicationVersion: string;
  Make: string;
  Model: string;
  SerialNumber: string;
  CreatedOn: Date;
}

export interface Device {
  DeviceId: number;
  Name: string;
  DeviceType: DeviceTypes;
  StatusType: number;
  DeviceTrackings: DeviceTracking[];
  ConnectionId?: string | null;
  Settings?: DeviceSettings | null;
  SerialNumber?: string | null;
}

export interface Settings {
  name: string;
  resourceName: string;
  dataType: string;
  value: string;
}

export interface KnownDeviceSettings {
  appSettings: Settings[];
  UpdateDateTime: Date | string;
}

export interface UnknownDeviceSettings {
  [index: string]: {
    groupName: string;
    settings: Settings[];
  };
}

export type DeviceSettings = KnownDeviceSettings & UnknownDeviceSettings;

export interface LegacyDeviceResponse {
  Device: {
    DeviceId: number;
    Name: string;
    LocationId: number;
    MerchantId?: number | null;
    DeviceType: DeviceTypes;
    StatusType: number;
  };
}

export interface LegacyDeviceManageRequest {
  DeviceId?: number | null;
  Name: string;
  DisplayCode: string | null;
  LocationId?: number;
  MerchantId?: number;
  DeviceType?: DeviceTypes;
}

export interface CreateDevicePreRegistrationRequest {
  Name: string;
  SerialNumber: string;
}

@Injectable({
  providedIn: 'root',
})
export class DeviceService extends BaseApiService {
  private readonly deviceEndpoint = '/v2/Devices';
  private readonly deviceLegacyEndpoint = '/Device';

  private readonly legacyBaseUrl$: Observable<string> = this.http
    .get<{ legacyServiceApiBaseUrl?: string }>(`assets/config/${getEnvironment()}/config.${getEnvironment()}.json`)
    .pipe(
      map((config) => config?.legacyServiceApiBaseUrl ?? ''),
      shareReplay(1)
    );

  findAll() {
    return this.get<DevicesResponse>(`${this.deviceEndpoint}`).pipe(
      catchError((err) => {
        console.error(err);
        const emptyDevicesResponse: DevicesResponse = { $values: [] };
        return of(emptyDevicesResponse);
      })
    );
  }

  findUnique(deviceId: number) {
    return this.get<DeviceResponse>(`${this.deviceEndpoint}/${deviceId}`).pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  create(newDevice: LegacyDeviceManageRequest) {
    return this.legacyBaseUrl$.pipe(
      switchMap((baseUrl) =>
        this.http.post<LegacyDeviceResponse>(`${baseUrl}${this.deviceLegacyEndpoint}/Save`, {
          ...newDevice,
        })
      ),
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  update(updatedDevice: LegacyDeviceManageRequest) {
    // This shouldn't be a POST, but legacy is dumb and uses a POST for an update
    return this.legacyBaseUrl$.pipe(
      switchMap((baseUrl) =>
        this.http.post<LegacyDeviceResponse>(`${baseUrl}${this.deviceLegacyEndpoint}/Save`, {
          ...updatedDevice,
        })
      ),
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  triggerBug(deviceId: number) {
    return this.post(`${this.deviceEndpoint}/${deviceId}/bug`, null);
  }

  updateDeviceSettings(deviceId: number, settings: string) {
    return this.put(`${this.deviceEndpoint}/settings?deviceId=${deviceId}`, settings).pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  createDevicePreRegistration(devicePreRegistration: CreateDevicePreRegistrationRequest) {
    return this.post(`${this.deviceEndpoint}/create-device-pre-registration`, devicePreRegistration).pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  updateDevicePreRegistration(deviceId: number, serialNumber: string) {
    return this.put(`${this.deviceEndpoint}/update-device-pre-registration?deviceId=${deviceId}`, {
      SerialNumber: serialNumber,
    }).pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }

  deleteDevicePreRegistration(deviceId: number) {
    return this.delete(`${this.deviceEndpoint}/delete-device-pre-registration?deviceId=${deviceId}`).pipe(
      catchError((err) => {
        console.error(err);
        return of(null);
      })
    );
  }
}
